// apps/hr/src/layouts/HRLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  Receipt,
  FileText,
  LogOut,
  Menu,
  X,
  ExternalLink,
  Briefcase,
  Wallet,
  Settings,
  Layers,
  ClipboardList,
  BarChart3,
  UserPlus,
  UserCog,
  ShieldCheck,
  Coins, // Added for Freelancer Earnings
} from 'lucide-react';
import { cn } from '../lib/utils';

// ✅ নতুন লোগো URL ব্যবহার করুন
const LOGO_URL = 'https://housedesigns99.com/api/files/f5eolh49d789mqi/rwth7jtsmlcst1n/qudexa_logo_fevicon_cmzEb5UuHA.png';

const HRLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRole = user?.role || 'hr';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const menuGroups = [
    {
      title: 'HR Management',
      icon: Briefcase,
      iconColor: 'text-blue-400',
      items: [
        { path: '/', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-400' },
        { path: '/employees', label: 'Employees', icon: Users, color: 'text-cyan-400' },
        { path: '/attendance', label: 'Attendance', icon: Calendar, color: 'text-amber-400' },
        { path: '/leave', label: 'Leave', icon: FileText, color: 'text-purple-400' },
      ],
    },
    {
      title: 'Leave Management',
      icon: ClipboardList,
      iconColor: 'text-purple-400',
      items: [
        { path: '/leave-requests', label: 'Leave Requests', icon: ClipboardList, color: 'text-purple-400' },
      ],
    },
    {
      title: 'Finance',
      icon: Wallet,
      iconColor: 'text-amber-400',
      items: [
        { path: '/salary', label: 'Salary', icon: DollarSign, color: 'text-green-400' },
        { path: '/promotions', label: 'Promotions', icon: TrendingUp, color: 'text-lime-400' },
        { path: '/expenses', label: 'Expenses', icon: Receipt, color: 'text-rose-400' },
        // ✅ Added Freelancer Earnings here
        { path: '/freelancer-earnings', label: 'Freelancer Earnings', icon: Coins, color: 'text-emerald-400' },
      ],
    },
    {
      title: 'Reports & Analytics',
      icon: BarChart3,
      iconColor: 'text-indigo-400',
      items: [
        { path: '/reports', label: 'Reports', icon: FileText, color: 'text-indigo-400' },
        { path: '/monthly-report', label: 'Monthly Report', icon: BarChart3, color: 'text-indigo-400' },
      ],
    },
    {
      title: 'Recruitment',
      icon: UserPlus,
      iconColor: 'text-green-400',
      items: [
        { path: '/recruitment', label: 'Recruitment', icon: UserPlus, color: 'text-green-400' },
      ],
    },
    {
      title: 'Employee Self Service',
      icon: UserCog,
      iconColor: 'text-cyan-400',
      items: [
        { path: '/my-profile', label: 'My Profile', icon: UserCog, color: 'text-cyan-400' },
      ],
    },
  ];

  const adminGroup = {
    title: 'Admin',
    icon: ShieldCheck,
    iconColor: 'text-rose-400',
    items: [
      { path: '/admin-settings', label: 'Settings', icon: Settings, color: 'text-rose-400' },
      { path: '/admin-users', label: 'User Management', icon: Users, color: 'text-rose-400' },
    ],
    externalItems: [
      { label: 'Admin Panel', url: 'http://localhost:3002', icon: ShieldCheck },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
          <span className="text-xl font-bold text-slate-800">
            HR <span className="text-amber-500">Panel</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">{user?.name || 'User'}</span>
          <span className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full font-medium">
            {userRole}
          </span>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 transition text-sm px-2 py-1 hover:bg-red-50 rounded-lg"
          >
            ⏻
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <button
          className="lg:hidden fixed bottom-4 left-4 z-50 p-2 bg-white rounded-md shadow"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>

        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-gray-900 to-black text-white shadow-2xl transform transition-transform duration-300 lg:translate-x-0 flex flex-col",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex justify-between items-center p-5 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
              <h1 className="text-xl font-bold tracking-tight">HR Panel</h1>
            </div>
            <button className="lg:hidden p-1 text-white/70 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-white/10 shrink-0">
            <p className="text-xs font-medium text-indigo-300 uppercase tracking-wider">Logged in as</p>
            <p className="font-semibold text-white">{user?.name || 'User'} <span className="text-indigo-300 text-sm">({userRole})</span></p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-4">
            {menuGroups.map((group, idx) => (
              <div key={idx} className="mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-300">
                  <group.icon className={`w-4 h-4 ${group.iconColor || 'text-indigo-300'}`} />
                  <span>{group.title}</span>
                </div>
                <div className="space-y-1 mt-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                        isActive(item.path)
                          ? "bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/10 border-r-4 border-amber-400"
                          : "text-indigo-200 hover:bg-white/10 hover:text-white"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`w-5 h-5 ${item.color || 'text-indigo-300'} flex-shrink-0`} />
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {isAdmin && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-rose-300">
                  <ShieldCheck className="w-4 h-4 text-rose-400" />
                  <span>Admin</span>
                </div>
                <div className="space-y-1 mt-1">
                  {adminGroup.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                        isActive(item.path)
                          ? "bg-rose-500/20 text-rose-300 shadow-lg shadow-rose-500/10 border-r-4 border-rose-400"
                          : "text-indigo-200 hover:bg-white/10 hover:text-white"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`w-5 h-5 ${item.color || 'text-rose-400'} flex-shrink-0`} />
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  ))}
                  {adminGroup.externalItems?.map((item) => (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-indigo-200 hover:bg-white/10 hover:text-white transition-all duration-200"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5 text-rose-400 flex-shrink-0" />
                      <span className="whitespace-nowrap">{item.label}</span>
                      <ExternalLink className="w-3 h-3 ml-auto text-indigo-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 shrink-0 space-y-2">
            <a
              href="http://localhost:3000"
              className="flex items-center gap-3 px-3 py-2 text-indigo-200 hover:bg-white/10 hover:text-white rounded-lg transition"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Homepage</span>
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-lg transition"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Logout</span>
            </button>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto p-6 lg:ml-72">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default HRLayout;